import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDay } from "utils/helpers";
import FileView from "components/fileView";
import DocumentViewerModal, { DocumentData } from "components/DocumentViewer";
import { LoadingIcon } from "utils/constant";
import ShowUI, { expandRoles, useTeamRoles, LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { useToast } from "contexts/ToastContext";
import {
  LAW_IMPLEMENTATION_REQUEST_STATUS,
  LEGAL_ADVISORY_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_STATUS,
} from "utils/constantValues";
import HeadRemark from "./headRemark";
import DirectorRemark from "./directorRemark";
import ProsecutorRemark from "./prosecutorRemark";
import ActionAvatars from "pages/conductLegalResearch/fragments/actionAvatars";
import PublicFeedback from "./publicFeedback";
import UploadAmendedFile from "./uploadAmendedFile";
import UploadEnforcementAnalysis from "./uploadEnforcementAnalysis";
import { useAuth } from "contexts/AuthContext";
import { StatusChip } from "components/StatusChip";
import LegislativeRespondBackForm from "./LegislativeResponback";
import TeamView from "components/team_view";
import {
  createAuditLawImplementation,
  updateLawImplementation,
  updateLawImplementationTargets,
} from "services/lawImplementationService";
import TargetList from "./TargetList";
import ConfirmationModal from "components/ConfirmationModal1";
import TeamEditPopup from "./teamEditPopup";

function LawImplementationDetail({ request }: { request: any }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [publicFeedback, setPublicFeedback] = useState(false);
  const [uploadAmend, setUploadAmend] = useState(false);
  const [uploadEnforcementAnalysis, setUploadEnforcementAnalysis] = useState(false);
  const [auditDraft, setAuditDraft] = useState(false);
  const [showDraftHistory, setShowDraftHistory] = useState(false);
  const [viewTeam, setViewTeam] = useState(false);
  const [edit, setEdit] = useState(false);
  const [teamId, setTeamId] = useState(0);
  const [team, setTeam] = useState<any | null>(null);
  const [prosecutorData, setProsecutor] = useState<any | null>(null);
  const [submit, setSubmit] = useState(false);
  const [formState, setFormState] = useState({ ...request });
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);

  const contextData = useAuth();
  // Prosecutor actions go to the team leader only (TeamScope on the page).
  const actingRoles = useTeamRoles();
  const { showToast } = useToast();
  const [teamToView, setTeamToView] = useState<{
    isOpen: boolean;
    members: any[];
    title: string;
  }>({
    isOpen: false,
    members: [],
    title: "",
  });
  const [isRespondBackFormOpen, setIsRespondBackFormOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingForwardStatus, setPendingForwardStatus] = useState<
    string | null
  >(null);
  const [showTeamEditPopup, setShowTeamEditPopup] = useState(false);
  const [selectedTeamForEdit, setSelectedTeamForEdit] = useState(null);
  const [accordionStates, setAccordionStates] = useState({
    documents: true,
    enforcement: true,
    evidence: true,
    audit: true,
    sectorFeedback: true,
    remarks: true,
    prosecutor: true,
  });
  const [enforcementDocViewerOpen, setEnforcementDocViewerOpen] = useState(false);
  const [enforcementDocuments, setEnforcementDocuments] = useState<DocumentData[]>([]);
  const [auditDocViewerOpen, setAuditDocViewerOpen] = useState(false);
  const [auditDocuments, setAuditDocuments] = useState<DocumentData[]>([]);
  const [evidenceDocViewerOpen, setEvidenceDocViewerOpen] = useState(false);
  const [evidenceDocuments, setEvidenceDocuments] = useState<DocumentData[]>([]);

  const toggleAccordion = (key: keyof typeof accordionStates) => {
    setAccordionStates(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleOpenTeamEdit = (team: any) => {
    const teamData = {
      ...team.prosecutors[0],
      followup_team_name: [team],
      prosecutors: team.prosecutors,
    };

    setSelectedTeamForEdit(teamData);
    setShowTeamEditPopup(true);
  };

  const handleFileChange = (
    file: any,
    fieldName: keyof Pick<typeof formState, "audit_summary_file">
  ) => {
    setFormState((prev: any) => ({
      ...prev,
      [fieldName]: file,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRespondBackClick = () => {
    setIsRespondBackFormOpen(!isRespondBackFormOpen);
  };

  const handleAdditionalInfoApprove = async () => {
    setLoading(true);
    const data = new FormData();

    data.append(
      "forward_status",
      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_registrar.value
    );

    try {
      const response = await updateLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegalResearchProposalApproved"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchProposalApproval"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = () => {};

  const handleIdentification = async () => {
    setLoading(true);
    const data = new FormData();
    data.append(
      "current_approval_status",
      LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value
    );
    data.append(
      "forward_status",
      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value
    );

    try {
      const response = await updateLawImplementation(formState.id, data);
      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("IdentificationCompleted"), "success");
    } catch (error) {
      console.error("Error updating request:", error);
      showToast(t("FailedToUpdateRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleForward = async (froward_status: string) => {
    const data = new FormData();
    if (
      formState.current_approval_status === "Under review" &&
      froward_status === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
    ) {
      showToast(t("CannotForwardBeforeEditing"), "error");
      return;
    }
    if (
      formState.current_approval_status === "Back to Head" &&
      froward_status ===
        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director.value
    ) {
      showToast(t("CannotForwardBeforeEditing"), "error");
      return;
    }

    if (
      formState.current_approval_status === "Back to Director" &&
      froward_status ===
        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value
    ) {
      showToast(t("CannotForwardBeforeEditing"), "error");
      return;
    }

    // Director: persist the toggled accept flags before forwarding to head.
    if (
      formState.current_approval_status === LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value &&
      formState.forward_status === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director.value &&
      froward_status === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
    ) {
      try {
        for (const target of (formState.identified_law_sectors || [])) {
          const tData = new FormData();
          tData.append("sector_name", target.sector_name);
          tData.append("is_slected_to_audit", String(target.is_slected_to_audit));
          if (target.follow_up) tData.append("follow_up", target.follow_up);
          await updateLawImplementationTargets(target.id, tData);
        }
      } catch (error) {
        console.error("Error saving target selections:", error);
      }
    }

    data.append("forward_status", froward_status);

    try {
      const response = await updateLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegislativeDraftForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    const data = new FormData();
    data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.published.value);
    data.append(
      "forward_status",
      LEGAL_ADVISORY_REQUEST_STATUS.not_forwarded.value
    );

    try {
      const response = await updateLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegislativeDraftForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
    } finally {
      setLoading(false);
      setAuditDraft(false);
    }
  };

  const handleDraftApprove = async () => {
    const data = new FormData();
    if (formState.current_approval_status === "identification_in_progress") {
      data.append(
        "current_approval_status",
        LAW_IMPLEMENTATION_REQUEST_STATUS.identification_complete.value
      );
      data.append(
        "forward_status",
        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value
      );
    } else if (formState.current_approval_status === "proposal_submitted") {
      data.append(
        "current_approval_status",
        LAW_IMPLEMENTATION_REQUEST_STATUS.audit_in_progress.value
      );
      data.append(
        "forward_status",
        LEGAL_ADVISORY_REQUEST_STATUS.assigned_to_prosecutor.value
      );
    } else if (formState.current_approval_status === "audit_completed") {
      data.append(
        "current_approval_status",
        "audit_completed"
      );
      data.append(
        "forward_status",
        "forwarded_to_management"
      );
    } else {
      data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.draft_approved.value);
      data.append(
        "forward_status",
        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_registrar.value
      );
    }

    try {
      const response = await updateLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegislativeDraftForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalApprove = async () => {
    const data = new FormData();
    if (formState.current_approval_status === "audit_completed") {
      data.append(
        "current_approval_status",
        LAW_IMPLEMENTATION_REQUEST_STATUS.finalization_complete.value
      );
      data.append(
        "forward_status",
        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_registrar.value
      );
    } else {
      data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.final_approval.value);
    }

    try {
      const response = await updateLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegislativeDraftForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTargets = async () => {
    try {
      const targets = formState.identified_law_sectors || [];
      // For a zonal audit the ZonalLIF accepts targets directly (not at the
      // forwarded_to_head stage), so the sector/audit records must still be created.
      const isZonal =
        formState?.is_zonal_audit === true ||
        formState?.is_zonal === true ||
        contextData?.user?.roles?.some(
          (r: any) =>
            r?.toLowerCase() === "zonallif" || r?.toLowerCase() === "zonallrd"
        );
      const shouldCreateAudit = (target: any) =>
        target.is_slected_to_audit &&
        (formState.forward_status ===
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value ||
          isZonal);

      let internal_response: any;

      for (const target of targets) {
        if (shouldCreateAudit(target)) {
          const formData = new FormData();
          formData.append("requested_by", formState.requested_by);
          formData.append(
            "forward_status",
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value
          );
          formData.append(
            "current_approval_status",
            LAW_IMPLEMENTATION_REQUEST_STATUS.identification_complete.value
          );
          formData.append(
            "identifies_specific_laws_government_sectors",
            target.sector_name
          );
          formData.append("description", formState.description);
          // Link the audit follow-up back to its parent law-implementation follow-up
          formData.append("follow_up", formState.id);
          // Store the parent follow-up's application number as the registry number
          if (formState.application_number)
            formData.append("registry_number", formState.application_number);
          // Store the related sector name in assigned_coordinator_name
          formData.append("assigned_coordinator_name", target.sector_name);
          // Mark the audit as zonal when the parent follow-up is zonal (or created by a zonal office).
          if (isZonal) formData.append("is_zonal_audit", "true");
          internal_response = await createAuditLawImplementation(formData);
        }
        const formData = new FormData();
        console.log(target);
        formData.append("sector_name", target.sector_name);
        formData.append(
          "is_slected_to_audit",
          String(target.is_slected_to_audit)
        );
        formData.append("follow_up", target.follow_up);
        if (shouldCreateAudit(target) && internal_response?.id)
          formData.append("audit_follow_up", internal_response.id);

        await updateLawImplementationTargets(target.id, formData);
      }

      const data = new FormData();

      if (
        formState.forward_status ===
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value ||
        isZonal
      )
        data.append(
          "current_approval_status",
          LAW_IMPLEMENTATION_REQUEST_STATUS.identification_complete.value
        );
      else
        data.append(
          "current_approval_status",
          LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value
        );
      const response = await updateLawImplementation(formState.id, data);

      setFormState((prev: any) => ({
        ...prev,
        ...response,
      }));
      showToast(t("AcceptedTargets"), "success");
      setEdit(false);
      setSubmit(true);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    }
  };

  useEffect(() => {
    const userId = contextData.user.user_id;

    const currentProsecutor = formState.teams?.find(
      (member: any) => member.user_id === userId
    );

    if (currentProsecutor) {
      const userTeamIds =
        currentProsecutor.followup_team_name?.map((team: any) => team.id) || [];

      const firstTeamId = userTeamIds[0];
      if (firstTeamId) {
        const teamMembers =
          formState.teams?.filter((member: any) => {
            const memberTeamIds =
              member.followup_team_name?.map((team: any) => team.id) || [];
            return memberTeamIds.includes(firstTeamId);
          }) || [];

        const teamInfo = currentProsecutor.followup_team_name?.find(
          (team: any) => team.id === firstTeamId
        );

        const teamObject = {
          id: firstTeamId,
          prosecutors: teamMembers,
          teamInfo: teamInfo,
          followup_team_name: currentProsecutor.followup_team_name,
          team_type: currentProsecutor.team_type,
        };

        setTeam(teamObject);
        setProsecutor(currentProsecutor);
      }
    } else {
      setTeam(null);
      setProsecutor(null);
    }
  }, [formState, contextData.user.user_id]);

  return (
    <div className="flex-1 bg-gray-100 mx-4 rounded-lg overflow-y-scroll scrollbar-hide">
      <div className="flex flex-col max-w-4xl bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 mb-3">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate("/law-implementation")}
              className="text-gray-600 hover:text-gray-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-gray-800 ml-8">
            {t("RequestForLawImplementation")}
          </h1>
          <div className="ml-auto">
            <StatusChip status={formState.current_approval_status} />
            <StatusChip status={formState.forward_status} />
          </div>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.registrar_forward_to_coordinator
                .value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="ml-auto flex gap-4">
              <button
                onClick={() => {
                  setPendingForwardStatus(
                    LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value
                  );
                  setShowConfirmModal(true);
                }}
                type="button"
                className="bg-[#212121] text-white rounded-lg px-4"
              >
                {t("Forward")}
              </button>
            </div>
          </ShowUI>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.stakeholder_forward_to_head.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div
                onClick={() =>
                  navigate("/legal-advisory/request", {
                    state: { request: formState },
                  })
                }
                className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <svg
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                    fill="black"
                  />
                </svg>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setPendingForwardStatus(
                      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
                    );
                    setShowConfirmModal(true);
                  }}
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_prosecutor.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setPendingForwardStatus(
                      LEGAL_ADVISORY_REQUEST_STATUS.assigned_to_prosecutor.value
                    );
                    setShowConfirmModal(true);
                  }}
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_coordinator.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setPendingForwardStatus(
                      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator
                        .value
                    );
                    setShowConfirmModal(true);
                  }}
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_director.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <ShowUI
                ui_key={
                  LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director
                    .value
                }
                currentStatus={formState.current_approval_status}
                forwardStatus={formState.forward_status}
              >
                <div
                  onClick={() => setEdit(!edit)}
                  className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                >
                  {edit ? (
                    <p>{t("Cancel")}</p>
                  ) : (
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                        fill="black"
                      />
                    </svg>
                  )}
                </div>
              </ShowUI>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setPendingForwardStatus(
                      formState.current_approval_status ===
                        LEGAL_ADVISORY_REQUEST_STATUS.under_review.value
                        ? LEGAL_ADVISORY_REQUEST_STATUS.not_forwarded.value
                        : LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director
                            .value
                    );
                    setShowConfirmModal(true);
                  }}
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.director_forward_to_coordinator
                .value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div
                onClick={() => setEdit(!edit)}
                className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                {edit ? (
                  <p>{t("Cancel")}</p>
                ) : (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                      fill="black"
                    />
                  </svg>
                )}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setPendingForwardStatus(
                      formState.current_approval_status ===
                        LEGAL_ADVISORY_REQUEST_STATUS.back_to_head.value
                        ? LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
                        : LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator
                            .value
                    );
                    setShowConfirmModal(true);
                  }}
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_prosecutor
                .value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <ShowUI
                ui_key={
                  LEGISLATIVE_DRAFTING_UI_RULES
                    .edit_coordinator_forward_to_prosecutor.value
                }
                currentStatus={formState.current_approval_status}
                forwardStatus={formState.forward_status}
              >
                <div
                  onClick={() => setEdit(!edit)}
                  className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                >
                  {edit ? (
                    <p>{t("Cancel")}</p>
                  ) : (
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                        fill="black"
                      />
                    </svg>
                  )}
                </div>
              </ShowUI>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setPendingForwardStatus(
                      formState.current_approval_status ===
                        LEGAL_ADVISORY_REQUEST_STATUS.back_to_director.value
                        ? LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator
                            .value
                        : LEGAL_ADVISORY_REQUEST_STATUS.assigned_to_prosecutor
                            .value
                    );
                    setShowConfirmModal(true);
                  }}
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_head.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div
              onClick={() => {
                setEdit(!edit);
                setSubmit(!submit);
              }}
              className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer"
            >
              {edit ? (
                <p>{t("Cancel")}</p>
              ) : (
                <svg
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                    fill="black"
                  />
                </svg>
              )}
            </div>
            {(() => {
              const isIdentification = formState.current_approval_status === LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value;
              const isCoordinatorStage = isIdentification && formState.forward_status === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value;
              const isDirectorStage = isIdentification && formState.forward_status === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director.value;
              // For a zonal audit the ZonalLIF is the sole authority: at the coordinator
              // stage they approve the accepted targets directly instead of forwarding to the director.
              const isZonal =
                formState?.is_zonal_audit === true ||
                contextData?.user?.roles?.some(
                  (r: any) => r?.toLowerCase() === "zonallif"
                );
              const zonalApprove = isZonal && isCoordinatorStage;
              // Coordinator forwards to the director (only after submitting accepted targets);
              // the director (and other stages) forward to the head.
              const target = isCoordinatorStage
                ? LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director.value
                : LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value;
              const label = zonalApprove
                ? (t("ApproveAcceptedTargets") || "Approve Accepted Targets")
                : isCoordinatorStage
                ? (t("ForwardToDirector") || "Forward to Director")
                : isDirectorStage
                  ? (t("ForwardToHead") || "Forward to Head")
                  : t("Forward");
              const showForward = !isCoordinatorStage || submit;
              if (!showForward) return null;
              return (
                <div className="flex ml-auto gap-4">
                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        if (zonalApprove) {
                          handleDraftApprove();
                          return;
                        }
                        setPendingForwardStatus(target);
                        setShowConfirmModal(true);
                      }}
                      type="button"
                      className="inline-flex items-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] text-white font-semibold rounded-lg px-5 py-2.5 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        {zonalApprove ? (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        )}
                      </svg>
                      {label}
                    </button>
                  </div>
                </div>
              );
            })()}
          </ShowUI>
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.additional_info_action.value}
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setPendingForwardStatus(
                      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_registrar.value
                    );
                    setShowConfirmModal(true);
                  }}
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.prosecutor_forward_to_coordinator
                .value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setPendingForwardStatus(
                      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator
                        .value
                    );
                    setShowConfirmModal(true);
                  }}
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.identification_complete_forward
                .value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            {!(
              formState.current_approval_status === LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value &&
              formState.forward_status === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
            ) && (
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={() => handleIdentification()}
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("IdentificationCompleted")}
                </button>
              </div>
            </div>
            )}
          </ShowUI>
          <div></div>
        </div>
        <ShowUI
          ui_key={LEGISLATIVE_DRAFTING_UI_RULES.publish.value}
          currentStatus={formState.current_approval_status}
          forwardStatus={formState.forward_status}
        >
          <div className="flex mb-4">
            <button
              onClick={handlePublish}
              className="p-4 flex-1 bg-[#212121] text-white rounded-lg"
            >
              {t("BroadcastToPublicWebsite")}
            </button>
          </div>
        </ShowUI>
        <ShowUI
          ui_key={LEGISLATIVE_DRAFTING_UI_RULES.broadcasted.value}
          currentStatus={formState.current_approval_status}
          forwardStatus={formState.forward_status}
        >
          <div className="flex mb-4">
            <button
              className="p-4 flex-1 bg-[#212121] text-white rounded-lg"
              onClick={handlePublish}
            >
              {t("broadcasted")}
            </button>
          </div>
        </ShowUI>
        {formState?.legal_request_advice_file && (
          <div className="flex-1 bg-white mb-4 p-4 rounded-lg border border-gray-400">
            <div className="pb-2 border-b border-gray-200 mb-4">
              <p>{t("SummaryWritten")}</p>
            </div>
            <p className="text-[#747171] font-normal text-xs mb-3">
              {formatDay(formState?.created_at).rest}
            </p>
            <p className="font-regular text-base text-[#606060]">
              {formState?.legal_request_advice_summary}
            </p>
            <div className="flex border border-gray-200 p-2 rounded-lg my-4">
              <FileView
                title={t("DraftLawFile")}
                file={formState?.legal_request_advice_file}
              />
            </div>
          </div>
        )}
        <ShowUI
          ui_key={LEGISLATIVE_DRAFTING_UI_RULES.oagb_management_head_approve_respond.value}
          currentStatus={formState.current_approval_status}
          forwardStatus={formState.forward_status}
        >
          <LegislativeRespondBackForm
            request={formState}
            setLegalResearch={setFormState}
            isOpen={isRespondBackFormOpen}
            onClose={() => setIsRespondBackFormOpen(false)}
            forward={LEGAL_ADVISORY_REQUEST_STATUS.audit_responded.value}
          />
          <div className="flex gap-4">
            <button
              onClick={handleRespondBackClick}
              className="p-4 flex-1 bg-[#D4D4D4] text-[#212121] rounded-lg"
            >
              {t("RespondBack")}
            </button>
            <button
              onClick={handleFinalApprove}
              className="p-4 flex-1 bg-[#212121] text-white rounded-lg"
            >
              {t("FinalApproveDraftLaw")}
            </button>
          </div>
        </ShowUI>
        <ShowUI
          ui_key={
            LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director.value
          }
          currentStatus={formState.current_approval_status}
          forwardStatus={formState.forward_status}
        >
          <HeadRemark
            type="legal"
            setEdit={setEdit}
            setFormState={setFormState}
            formState={formState}
          />
        </ShowUI>
        <ShowUI
          ui_key={
            LEGISLATIVE_DRAFTING_UI_RULES.edit_director_forward_to_coordinator
              .value
          }
          currentStatus={formState.current_approval_status}
          forwardStatus={formState.forward_status}
        >
          <DirectorRemark
            type="legal"
            setEdit={setEdit}
            setFormState={setFormState}
            formState={formState}
          />
        </ShowUI>
        <div className="flex-1 overflow-y-scroll scrollbar-hide">
          {/* Documents */}
          <div className="mt-6 space-y-4 border border-gray-400 p-4 mb-8 rounded-lg">
            {formState?.request_letter_file && (
              <div className="flex items-center justify-between ">
                <FileView
                  title={t("RequestLetter")}
                  file={formState?.request_letter_file}
                />
              </div>
            )}
            <div className="mb-8">
              {prosecutorData?.sectors && (
                <TargetList
                  formState={prosecutorData?.sectors}
                  status={formState.current_approval_status}
                  setFormState={setFormState}
                  edit={edit}
                />
              )}
              {!prosecutorData?.sectors && (
                <TargetList
                  formState={formState?.identified_law_sectors}
                  status={formState.current_approval_status}
                  setFormState={setFormState}
                  edit={edit}
                  teams={formState.teams}
                  forwardStatus={formState.forward_status}
                  onSubmitAccepted={handleDraftApprove}
                />
              )}
              {!submit && (
                <div className="flex w-full">
                  <ShowUI
                    ui_key={
                      LEGISLATIVE_DRAFTING_UI_RULES.edit_target_approve.value
                    }
                    currentStatus={formState.current_approval_status}
                    forwardStatus={formState.forward_status}
                  >
                    {(() => {
                      const hasSelected = (formState?.identified_law_sectors || []).some(
                        (target: any) => target.is_slected_to_audit
                      );
                      return (
                        <button
                          type="button"
                          onClick={handleAcceptTargets}
                          disabled={!hasSelected}
                          className="inline-flex items-center justify-center gap-2 ml-auto mb-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-[#0A1D39]/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          {t("SubmitAcceptedTargets") || "Submit Accepted Targets"}
                        </button>
                      );
                    })()}
                  </ShowUI>
                </div>
              )}
              <div className="bg-[#f5f5f5] p-6 rounded-lg border border-[#d1d5db] mb-6">
                <h1 className="text-[#000000] text-xl font-bold mb-4 border-b border-[#d1d5db] pb-2">
                  {t("AuditTargetOverview")}
                </h1>
                <div className="space-y-4">
                  <div>
                    <p className="text-[#333333] font-semibold text-sm uppercase tracking-wide">{t("AuditTarget")}</p>
                    <p className="text-[#000000] font-medium text-base mt-1">
                      {formState.audit_target}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#333333] font-semibold text-sm uppercase tracking-wide">{t("Description")}</p>
                    <p className="text-[#000000] font-medium text-base mt-1">
                      {formState?.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Team Submission Status - Only for Coordinators */}
              {(contextData.user.roles?.some((role: any) => role === "LegalResearchCoordinator") || contextData.user.roles?.some((role: any) => role?.toLowerCase() === "zonallif")) && (
                <div className="mt-6 mb-4">
                  <h2 className="text-[#000000] text-xl font-bold mb-6 border-b-2 border-[#d1d5db] pb-2">{t("TeamSubmissionStatus")}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {formState.teams?.map((team: any) => {
                      const hasSubmitted = formState.identified_law_sectors?.some((sector: any) =>
                        team.prosecutors?.some((p: any) => p.id.toString() === sector.followup_team?.toString())
                      );
                      return (
                        <div key={team.id} className={`p-6 rounded-lg border-2 shadow-md ${hasSubmitted ? 'bg-[#f0fdf4] border-[#10b981]' : 'bg-[#fef2f2] border-[#dc2626]'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-[#000000] text-lg">{team.team_name}</p>
                              {(() => {
                                const leader = team.prosecutors?.find((p: any) => p.role === 'leader' || p.is_team_leader || p.isTeamLeader);
                                const leaderName = leader && (leader.user_name || leader.prosecutor_name || leader.name || leader.first_name || [leader.first_name, leader.last_name].filter(Boolean).join(" "));
                                return (
                                  <p className="text-sm text-[#333333] mt-1">{t("TeamLeader")}: {leaderName || t("NotAssigned")}</p>
                                );
                              })()}
                            </div>
                            <div className={`px-4 py-2 rounded-full text-sm font-semibold ${hasSubmitted ? 'bg-[#10b981] text-white' : 'bg-[#dc2626] text-white'}`}>
                              {hasSubmitted ? `✓ ${t("Submitted")}` : `✗ ${t("Pending")}`}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {formState?.teams && formState.teams.length > 0 && (
              <div className="mt-6 mb-6">
                {formState.teams
                  .filter((team: any) => {
                    if (expandRoles(contextData?.user?.roles || []).includes("LegalResearchProsecutor")) {
                      return team.prosecutors?.some(
                        (p: any) => String(p.user_id) === String(contextData?.user?.user_id)
                      );
                    }
                    return true;
                  })
                  .map((team: any, index: number) => (
                  <div
                    key={team.id}
                    className="flex items-center justify-between bg-gray-50 rounded-lg border border-gray-200 mb-2 p-3"
                  >
                    <div className="flex items-center w-full">
                      <div className="flex-1">
                        <ActionAvatars
                          title={team.team_name}
                          users={team.prosecutors}
                          prosecutor={true}
                        />
                      </div>
                      <div className="flex items-center ml-4">
                        <svg
                          onClick={() => handleOpenTeamEdit(team)}
                          className="cursor-pointer"
                          width="25"
                          height="24"
                          viewBox="0 0 25 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                            fill="black"
                          />
                        </svg>
                        <svg
                          onClick={() =>
                            setTeamToView({
                              isOpen: true,
                              members: team.prosecutors,
                              title: team.team_name,
                            })
                          }
                          className="cursor-pointer ml-2"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <mask
                            id={`mask_${team.id}`}
                            maskUnits="userSpaceOnUse"
                            x="1"
                            y="5"
                            width="22"
                            height="14"
                          >
                            <path
                              d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                              fill="white"
                              stroke="white"
                              strokeWidth="1.33333"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                              fill="black"
                              stroke="black"
                              strokeWidth="1.33333"
                              strokeLinejoin="round"
                            />
                          </mask>
                          <g mask={`url(#mask_${team.id})`}>
                            <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                          </g>
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {((formState?.file_histories?.find(
            (file: any) => file.file_type === "enforcement_analysis"
          )) || (formState?.documents?.find(
            (d: any) => d.document_type === "enforcement_analysis_files"
          ))) && (
            <div className="mt-6 border border-blue-300 bg-blue-50 p-4 mb-8 rounded-lg shadow-sm">
              <div className="flex items-center justify-between cursor-pointer pb-2 border-b border-blue-200" onClick={() => toggleAccordion('enforcement')}>
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-blue-900">{t("EnforcementAnalysis")}</h3>
                </div>
                <svg className={`w-5 h-5 text-blue-700 transform transition-transform ${accordionStates.enforcement ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {accordionStates.enforcement && (
                <div className="mt-4 space-y-4">
                  {formState?.registry_number && (
                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                      <p className="text-blue-800 font-semibold text-sm">{t("RegisterNumber")}</p>
                      <p className="text-blue-900 font-medium">{formState.registry_number}</p>
                    </div>
                  )}
                  {formState?.documents?.some((d: any) => d.document_type === "audit_findings_files") && (
                    <div className="mt-2 mb-2">
                      <p className="text-blue-800 font-semibold text-sm mb-1">{t("AuditFindingsFiles")}</p>
                      <DocumentViewerModal
                        documents={formState.documents.filter((d: any) => d.document_type === "audit_findings_files")}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                      />
                    </div>
                  )}
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-blue-800 font-semibold text-sm">{t("EnforcementAnalysisSummary")}</p>
                      <div className="flex items-center gap-2">
                        {contextData?.user?.roles?.some(
                          (role: any) => role === "LegalResearchCoordinator"
                        ) || contextData?.user?.roles?.some((role: any) => role?.toLowerCase() === "zonallif") && (
                          <button
                            onClick={() => setUploadEnforcementAnalysis(true)}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded border border-blue-300 hover:bg-blue-50"
                          >
                            {t("Edit")}
                          </button>
                        )}
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">{t("OfficialDocument")}</span>
                      </div>
                    </div>
                    <p className="text-gray-700 font-medium">{formState?.enforcement_analysis_summary}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-blue-800 font-semibold text-sm">{t("AttachedFile")}</p>
                      {(contextData?.user?.roles?.some(
                        (role: any) => role === "LegalResearchCoordinator"
                      ) || contextData?.user?.roles?.some((role: any) => role?.toLowerCase() === "zonallif")) && (
                        <button
                          onClick={() => setUploadEnforcementAnalysis(true)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded border border-blue-300 hover:bg-blue-50"
                        >
                          {t("UpdateFile")}
                        </button>
                      )}
                    </div>
                    {(() => {
                      const docs: any[] =
                        formState?.documents?.filter((d: any) =>
                          d.document_type === "enforcement_analysis_files"
                        ) || [];
                      const legacy: any[] =
                        formState?.file_histories
                          ?.filter((f: any) => f.file_type === "enforcement_analysis")
                          .map((f: any) => ({
                            uu_id: f.id?.toString() || "1",
                            document_type: "enforcement_analysis",
                            document_uuid: f.id?.toString() || "1",
                            document_path: f.enforcement_analysis_file_details,
                            created_at: f.created_at || new Date().toISOString(),
                            updated_at: f.updated_at || new Date().toISOString(),
                          })) || [];

                      const allDocs = [...docs, ...legacy];
                      const sorted = allDocs.sort(
                        (a, b) =>
                          new Date(b.created_at).getTime() -
                          new Date(a.created_at).getTime()
                      );

                      if (sorted.length === 0) {
                        return (
                          <p className="text-gray-500">
                            {t("NoEnforcementAnalysisFile")}
                          </p>
                        );
                      }

                      const latest = sorted[0];
                      const previous = sorted.slice(1);

                      return (
                        <>
                          <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                              {t("LatestVersion")}
                            </p>
                            <DocumentViewerModal
                              documents={[latest]}
                              baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                            />
                          </div>

                          {previous.length > 0 && (
                            <div>
                              <details className="border border-gray-300 rounded-lg">
                                <summary className="p-3 cursor-pointer bg-gray-50 hover:bg-gray-100 rounded-lg">
                                  <span className="font-medium">
                                    {t("PreviousVersions", { count: previous.length })}
                                  </span>
                                </summary>
                                <div className="p-3 space-y-3">
                                  {previous.map((doc: any, index: number) => (
                                    <div
                                      key={doc.document_uuid}
                                      className="border border-gray-200 rounded-lg p-2"
                                    >
                                      <p className="text-xs text-gray-500 mb-1">
                                        {t("VersionNumber", { version: previous.length - index, date: new Date(doc.created_at).toLocaleDateString() })}
                                      </p>
                                      <DocumentViewerModal
                                        documents={[doc]}
                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                                        hideEmptyFile={true}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </details>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
          {formState?.evidences.length !== 0 && (
            <div className="mt-6 space-y-4 border border-gray-400 p-4 mb-8 rounded-lg">
              <div className="flex gap-2">
                <p className="text-[#432101AB] font-normal text-base">
                  {t("Evidence")}
                </p>
                {formState?.evidences && (
                  <div className="flex ml-auto">
                    <p className="text-gray-500">{t("History")}</p>
                    <svg
                      onClick={() => setShowDraftHistory(!showDraftHistory)}
                      className="my-auto ml-2 h-8"
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="8"
                      viewBox="0 0 10 8"
                      fill="none"
                    >
                      <path
                        d="M5 8L0.669872 0.499999L9.33013 0.5L5 8Z"
                        fill="#D9D9D9"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <p className="text-gray-500 font-medium mb-2">
                {formState?.evidences?.[0]?.summary_of_evidence}
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-gray-200 border border-gray-800 p-2 rounded-md">
                    <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
                      <path d="M6.76745 15.4646H5.80078V13.5312H6.76745C7.02382 13.5312 7.2697 13.6331 7.45098 13.8144C7.63227 13.9957 7.73411 14.2415 7.73411 14.4979C7.73411 14.7543 7.63227 15.0002 7.45098 15.1815C7.2697 15.3627 7.02382 15.4646 6.76745 15.4646ZM13.5341 19.3313V13.5312H14.5008C14.7572 13.5312 15.003 13.6331 15.1843 13.8144C15.3656 13.9957 15.4674 14.2415 15.4674 14.4979V18.3646C15.4674 18.621 15.3656 18.8668 15.1843 19.0481C15.003 19.2294 14.7572 19.3313 14.5008 19.3313H13.5341Z" fill="#432101" />
                      <path fillRule="evenodd" clipRule="evenodd" d="M1.93359 2.9C1.93359 2.13087 2.23913 1.39325 2.78298 0.84939C3.32684 0.305535 4.06447 0 4.83359 0L20.7005 0L27.0669 6.36647V26.1C27.0669 26.8691 26.7614 27.6068 26.2175 28.1506C25.6737 28.6945 24.9361 29 24.1669 29H4.83359C4.06447 29 3.32684 28.6945 2.78298 28.1506C2.23913 27.6068 1.93359 26.8691 1.93359 26.1V2.9ZM6.76693 11.6H3.86693V21.2667H5.80026V17.4H6.76693C7.53605 17.4 8.27368 17.0945 8.81754 16.5506C9.36139 16.0068 9.66693 15.2691 9.66693 14.5C9.66693 13.7309 9.36139 12.9932 8.81754 12.4494C8.27368 11.9055 7.53605 11.6 6.76693 11.6ZM14.5003 11.6H11.6003V21.2667H14.5003C15.2694 21.2667 16.007 20.9611 16.5509 20.4173C17.0947 19.8734 17.4003 19.1358 17.4003 18.3667V14.5C17.4003 13.7309 17.0947 12.9932 16.5509 12.4494C16.007 11.9055 15.2694 11.6 14.5003 11.6ZM19.3336 21.2667V11.6H25.1336V13.5333H21.2669V15.4667H23.2003V17.4H21.2669V21.2667H19.3336Z" fill="#432101" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium">{t("Evidence")}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const evidenceFile = formState?.evidences?.[0];
                    if (evidenceFile?.file) {
                      setEvidenceDocuments([
                        {
                          uu_id: evidenceFile.id?.toString() || "1",
                          document_type: "evidence",
                          document_uuid: evidenceFile.id?.toString() || "1",
                          document_path: evidenceFile.file,
                          created_at: evidenceFile.created_at || new Date().toISOString(),
                          updated_at: evidenceFile.updated_at || new Date().toISOString(),
                        },
                      ]);
                      setEvidenceDocViewerOpen(true);
                    }
                  }}
                  className="ml-auto my-auto text-blue-600 text-sm font-medium hover:text-blue-800"
                >
                  {t("View")}
                </button>
              </div>

              {formState?.collectors && formState?.collectors.length !== 0 && (
                <div className="mt-6 mb-6 flex bg-gray-50 rounded-lg border border-gray-200">
                  <ActionAvatars
                    title={t("CollectorsList")}
                    users={formState?.collectors}
                    prosecutor={true}
                  />
                  {(contextData?.user?.roles?.some(
                    (p) => p === "LegalResearchCoordinator"
                  ) || contextData?.user?.roles?.some((p) => p?.toLowerCase() === "zonallif")) &&
                    (!edit ? (
                      <svg
                        onClick={() => setEdit(true)}
                        className="my-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer"
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                          fill="black"
                        />
                      </svg>
                    ) : (
                      <p
                        onClick={() => setEdit(false)}
                        className="my-auto mr-2 hover:bg-gray-300 rounded-lg px-4 cursor-pointer"
                      >
                        {t("Cancel")}
                      </p>
                    ))}
                  <svg
                    onClick={() =>
                      setTeamToView({
                        isOpen: true,
                        members: formState?.collectors || [],
                        title: t("CollectorsTeam"),
                      })
                    }
                    className="mr-2 ml-auto my-auto cursor-pointer"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <mask
                      id="mask0_9_31087"
                      maskUnits="userSpaceOnUse"
                      x="1"
                      y="5"
                      width="22"
                      height="14"
                    >
                      <path
                        d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                        fill="white"
                        stroke="white"
                        strokeWidth="1.33333"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                        fill="black"
                        stroke="black"
                        strokeWidth="1.33333"
                        strokeLinejoin="round"
                      />
                    </mask>
                    <g mask="url(#mask0_9_31087)">
                      <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                    </g>
                  </svg>
                </div>
              )}
            </div>
          )}
          {((formState?.audit_findings) || (formState?.documents?.find(
            (d: any) => d.document_type === "audit_findings_files"
          ))) && (
            <div className="mt-6 border border-blue-300 bg-blue-50 p-4 mb-8 rounded-lg shadow-sm">
              <div className="flex items-center justify-between cursor-pointer pb-2 border-b border-blue-200" onClick={() => toggleAccordion('audit')}>
                <div className="flex items-center space-x-2">
                  <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  <h3 className="text-lg font-semibold text-blue-900">{t("AuditExecutionDetail")}</h3>
                </div>
                <svg className={`w-5 h-5 text-blue-700 transform transition-transform ${accordionStates.audit ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {accordionStates.audit && (
                <div className="mt-4 space-y-4">
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-blue-800 font-semibold text-sm">{t("AuditFindings")}</p>
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">{t("OfficialDocument")}</span>
                    </div>
                    <p className="text-gray-700 font-medium">{formState?.audit_findings}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <p className="text-blue-800 font-semibold text-sm mb-2">{t("AttachedFile")}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="bg-gray-200 border border-gray-800 p-2 rounded-md">
                          <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
                            <path d="M6.76745 15.4646H5.80078V13.5312H6.76745C7.02382 13.5312 7.2697 13.6331 7.45098 13.8144C7.63227 13.9957 7.73411 14.2415 7.73411 14.4979C7.73411 14.7543 7.63227 15.0002 7.45098 15.1815C7.2697 15.3627 7.02382 15.4646 6.76745 15.4646ZM13.5341 19.3313V13.5312H14.5008C14.7572 13.5312 15.003 13.6331 15.1843 13.8144C15.3656 13.9957 15.4674 14.2415 15.4674 14.4979V18.3646C15.4674 18.621 15.3656 18.8668 15.1843 19.0481C15.003 19.2294 14.7572 19.3313 14.5008 19.3313H13.5341Z" fill="#432101" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M1.93359 2.9C1.93359 2.13087 2.23913 1.39325 2.78298 0.84939C3.32684 0.305535 4.06447 0 4.83359 0L20.7005 0L27.0669 6.36647V26.1C27.0669 26.8691 26.7614 27.6068 26.2175 28.1506C25.6737 28.6945 24.9361 29 24.1669 29H4.83359C4.06447 29 3.32684 28.6945 2.78298 28.1506C2.23913 27.6068 1.93359 26.8691 1.93359 26.1V2.9ZM6.76693 11.6H3.86693V21.2667H5.80026V17.4H6.76693C7.53605 17.4 8.27368 17.0945 8.81754 16.5506C9.36139 16.0068 9.66693 15.2691 9.66693 14.5C9.66693 13.7309 9.36139 12.9932 8.81754 12.4494C8.27368 11.9055 7.53605 11.6 6.76693 11.6ZM14.5003 11.6H11.6003V21.2667H14.5003C15.2694 21.2667 16.007 20.9611 16.5509 20.4173C17.0947 19.8734 17.4003 19.1358 17.4003 18.3667V14.5C17.4003 13.7309 17.0947 12.9932 16.5509 12.4494C16.007 11.9055 15.2694 11.6 14.5003 11.6ZM19.3336 21.2667V11.6H25.1336V13.5333H21.2669V15.4667H23.2003V17.4H21.2669V21.2667H19.3336Z" fill="#432101" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium">{t("AuditFinding")}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const docFile = formState?.documents?.find(
                            (d: any) => d.document_type === "audit_findings_files"
                          );
                          const auditFile = docFile?.document_path || formState?.audit_findings_file;
                          if (auditFile) {
                            setAuditDocuments([
                              {
                                uu_id: docFile?.uu_id || "1",
                                document_type: docFile?.document_type || "audit_finding",
                                document_uuid: docFile?.document_uuid || "1",
                                document_path: auditFile,
                                created_at: docFile?.created_at || new Date().toISOString(),
                                updated_at: docFile?.updated_at || new Date().toISOString(),
                              },
                            ]);
                            setAuditDocViewerOpen(true);
                          }
                        }}
                        className="ml-auto my-auto text-blue-600 text-sm font-medium hover:text-blue-800"
                      >
                        {t("View")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sector Feedback Section */}
          {((formState?.sector_feedback_audit_findings) || (formState?.documents?.find(
            (d: any) => d.document_type === "sector_feedback_files"
          ))) && (
            <div className="mt-6 border border-blue-300 bg-blue-50 p-4 mb-8 rounded-lg shadow-sm">
              <div className="flex items-center justify-between cursor-pointer pb-2 border-b border-blue-200" onClick={() => toggleAccordion('sectorFeedback')}>
                <div className="flex items-center space-x-2">
                  <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  <h3 className="text-lg font-semibold text-blue-900">{t("SectorFeedback")}</h3>
                </div>
                <svg className={`w-5 h-5 text-blue-700 transform transition-transform ${accordionStates.sectorFeedback ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {accordionStates.sectorFeedback && (
                <div className="mt-4 space-y-4">
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-blue-800 font-semibold text-sm">{t("SectorFeedbackSummary")}</p>
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">{t("OfficialDocument")}</span>
                    </div>
                    <p className="text-gray-700 font-medium">{formState?.sector_feedback_audit_findings}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <p className="text-blue-800 font-semibold text-sm mb-2">{t("AttachedFile")}</p>
                    {(() => {
                      const sectorDocs = formState?.documents?.filter((d: any) =>
                        d.document_type === "sector_feedback_files"
                      ) || [];
                      
                      if (sectorDocs.length === 0) {
                        return (
                          <p className="text-gray-500">
                            {t("NoSectorFeedbackFile")}
                          </p>
                        );
                      }

                      const latest = sectorDocs[0];
                      const previous = sectorDocs.slice(1);

                      return (
                        <>
                          <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                              {t("LatestVersion")}
                            </p>
                            <DocumentViewerModal
                              documents={[latest]}
                              baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                            />
                          </div>

                          {previous.length > 0 && (
                            <div>
                              <details className="border border-gray-300 rounded-lg">
                                <summary className="p-3 cursor-pointer bg-gray-50 hover:bg-gray-100 rounded-lg">
                                  <span className="font-medium">
                                    {t("PreviousVersions", { count: previous.length })}
                                  </span>
                                </summary>
                                <div className="p-3 space-y-3">
                                  {previous.map((doc: any, index: number) => (
                                    <div
                                      key={doc.document_uuid}
                                      className="border border-gray-200 rounded-lg p-2"
                                    >
                                      <p className="text-xs text-gray-500 mb-1">
                                        {t("VersionNumber", { version: sectorDocs.length - 1 - index, date: new Date(doc.created_at).toLocaleDateString() })}
                                      </p>
                                      <DocumentViewerModal
                                        documents={[doc]}
                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                                        hideEmptyFile={true}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </details>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          <br />
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft.value}
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <LegislativeRespondBackForm
              request={formState}
              setLegalResearch={setFormState}
              isOpen={isRespondBackFormOpen}
              onClose={() => setIsRespondBackFormOpen(false)}
              forward={LEGAL_ADVISORY_REQUEST_STATUS.draft_responded.value}
              type="legal"
            />
            <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                type="button"
                className="flex-1 px-6 py-3 rounded-md bg-[#D4D4D4] text-[#212121] font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                onClick={handleRespondBackClick}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon} {t("Submitting")}
                  </div>
                ) : (
                  t("RespondBack")
                )}
              </button>
              <div
                onClick={() => {
                  setPendingForwardStatus(
                    LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director.value
                  );
                  setShowConfirmModal(true);
                }}
                className="flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#212121] text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon} {t("Submitting")}
                  </div>
                ) : (
                  t("Approve")
                )}
              </div>
            </div>
          </ShowUI>
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_director.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <LegislativeRespondBackForm
              request={formState}
              setLegalResearch={setFormState}
              isOpen={isRespondBackFormOpen}
              onClose={() => setIsRespondBackFormOpen(false)}
              forward={LEGAL_ADVISORY_REQUEST_STATUS.draft_responded.value}
              type="legal"
            />
            <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                type="button"
                className="flex-1 px-6 py-3 rounded-md bg-[#D4D4D4] text-[#212121] font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                onClick={handleRespondBackClick}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon} {t("Submitting")}
                  </div>
                ) : (
                  t("RespondBack")
                )}
              </button>
              {submit && (
                <div
                  onClick={() => {
                    setPendingForwardStatus(
                      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
                    );
                    setShowConfirmModal(true);
                  }}
                  className="flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#212121] text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      {LoadingIcon} {t("Submitting")}
                    </div>
                  ) : (
                    t("Approve")
                  )}
                </div>
              )}
            </div>
          </ShowUI>
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_head.value}
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <LegislativeRespondBackForm
              request={formState}
              setLegalResearch={setFormState}
              isOpen={isRespondBackFormOpen}
              onClose={() => setIsRespondBackFormOpen(false)}
              forward={LEGAL_ADVISORY_REQUEST_STATUS.draft_responded.value}
              type="legal"
            />

            <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                type="button"
                className="flex-1 px-6 py-3 rounded-md bg-[#D4D4D4] text-[#212121] font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                onClick={handleRespondBackClick}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon} {t("Submitting")}
                  </div>
                ) : (
                  t("RespondBack")
                )}
              </button>
              <div
                onClick={handleFinalApprove}
                className="flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#212121] text-white font-semibold hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-600"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon} {t("Submitting")}
                  </div>
                ) : (
                  t("Approve")
                )}
              </div>
            </div>
          </ShowUI>

          {/* Remark Section */}
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.internal_ui.value}
            currentStatus={"all"}
            forwardStatus={"all"}
          >
            {formState.remarks &&
              formState?.remarks?.find(
                (remark: any) => remark.role === "head"
              ) && (
                <div>
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("RemarkForDirector")}
                  </p>
                  <p className="text-gray-500 font-medium mb-2">
                    {
                      formState?.remarks
                        .filter((remark: any) => remark.role === "head")
                        .slice()
                        .reverse()[0]?.remark
                    }
                  </p>
                  <br />
                </div>
              )}
            {formState.remarks &&
              formState?.remarks?.find(
                (remark: any) => remark.role === "director"
              ) && (
                <div>
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("RemarkForCoordinator")}
                  </p>
                  <p className="text-gray-500 font-medium mb-2">
                    {
                      formState?.remarks
                        .filter((remark: any) => remark.role === "director")
                        .slice()
                        .reverse()[0]?.remark
                    }
                  </p>
                  <br />
                </div>
              )}
            {formState.remarks &&
              formState?.remarks?.find(
                (remark: any) => remark.role === "coordinator"
              ) && (
                <div>
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("RemarkForProsecutor")}
                  </p>
                  <p className="text-gray-500 font-medium mb-2">
                    {
                      formState?.remarks
                        .filter((remark: any) => remark.role === "coordinator")
                        .slice()
                        .reverse()[0]?.remark
                    }
                  </p>
                  <br />
                </div>
              )}
          </ShowUI>
        </div>
        {actingRoles.includes("LegalResearchProsecutor") && (
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.draft_and_additional_file.value}
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <ProsecutorRemark setFormState={setFormState} formState={formState} />
          </ShowUI>
        )}
      </div>
      {publicFeedback && (
        <PublicFeedback
          data={formState}
          setLegalResearch={setFormState}
          isOpen={publicFeedback}
          onClose={() => setPublicFeedback(false)}
        />
      )}
      {uploadAmend && (
        <UploadAmendedFile
          data={formState}
          setLegalResearch={setFormState}
          isOpen={uploadAmend}
          onClose={() => setUploadAmend(false)}
        />
      )}
      {uploadEnforcementAnalysis && (
        <UploadEnforcementAnalysis
          data={formState}
          setLegalResearch={setFormState}
          isOpen={uploadEnforcementAnalysis}
          onClose={() => setUploadEnforcementAnalysis(false)}
        />
      )}
      {teamToView.isOpen && (
        <TeamView
          teamMembers={teamToView.members}
          onClose={() =>
            setTeamToView({ isOpen: false, members: [], title: "" })
          }
        />
      )}
      {showTeamEditPopup && (
        <TeamEditPopup
          teamData={selectedTeamForEdit}
          onClose={() => {
            setShowTeamEditPopup(false);
            setSelectedTeamForEdit(null);
          }}
          onSuccess={(updatedData) => {
            showToast(t("TeamUpdatedSuccess"), "success");
          }}
        />
      )}
      <ConfirmationModal
        isOpen={showConfirmModal}
        isLoading={loading}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={() => {
          if (pendingForwardStatus) handleForward(pendingForwardStatus);
          setShowConfirmModal(false);
          setPendingForwardStatus(null);
        }}
        title={t("ConfirmForward")}
        message={t("ConfirmForwardMessage")}
        confirmText={t("Forward")}
        cancelText={t("Cancel")}
        type="warning"
      />
      <DocumentViewerModal
        label=""
        documents={enforcementDocuments}
        baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
        hide={!enforcementDocViewerOpen}
        showMetadata={true}
        layout="list"
      />
    </div>
  );
}

export default LawImplementationDetail;